/* The compositions — v3.3 migration, pack supports-b (WF-015 the library's contents · WF-019 / WF-080–085 the designer's
   template strip · WF-089 « Enregistrer comme modèle »).
   A template is a composition: it changes the ground, the bands and the type of a support — never the QR plate (black
   on white, the model's side, quiet zone kept: packages/brand/qr/README.md) and never the locked line. Six compositions,
   named as the reference names them; their grounds derive from the merchant's own accent (a tint) or a neutral paper —
   nothing of the reference's own colours is copied. « Essentiel » is the default: a design without `template` draws it.
   A saved model (WF-089) is a design without a token — its composition, its texts, its colours, its logo choice — kept in
   this browser per world (`bmr.modeles.<monde>`): no API exists for a library (contract 06 has none; a server library
   is Technical's later), and the QR is always the new support's own. */
import { currentWorld } from '@/mocks/worlds'
import { contrastRatio, supportPalette, SUPPORT_INK, SUPPORT_PAPER, type SupportDesign, type SupportTemplate } from '@/lib/campaign'

export const TEMPLATES: { id: SupportTemplate; name: string; line: string }[] = [
  { id: 'editorial', name: 'Éditorial', line: 'Un titre élégant et une composition aérée.' },
  { id: 'contraste', name: 'Contraste', line: 'Un message affirmé, avec un QR protégé.' },
  { id: 'douceur', name: 'Douceur', line: 'Des tons doux pour un accueil chaleureux.' },
  { id: 'essentiel', name: 'Essentiel', line: 'La marque, le message, le QR. Simple et lisible.' },
  { id: 'photo', name: 'Photo', line: 'Votre image de l’établissement, avec un espace protégé pour le QR.' },
  { id: 'promo', name: 'Mise en avant', line: 'Un message de participation, indépendant de tout avis.' },
]
export const templateOf = (d: SupportDesign): SupportTemplate => d.template ?? 'essentiel'
export const templateName = (t: SupportTemplate) => TEMPLATES.find(x => x.id === t)?.name ?? 'Essentiel'

/** the key of « Précision facultative » in a design's texts, and of the flag « Le texte contient une offre… » */
export const EXTRA_KEY = 'sup.extra'
export const OFFER_KEY = 'sup.offer'

/** a hex mixed towards white: `k` = the share of white (0.9 → a pale tint) */
export function tint(hex: string, k: number): string {
  const n = parseInt(hex.replace('#', '').padEnd(6, '0').slice(0, 6), 16)
  const c = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map(v => Math.round(v + (255 - v) * k))
  return `#${c.map(v => v.toString(16).padStart(2, '0')).join('')}`
}
/** the text colour that reads on a ground (the palette's own rule: ink unless the ground is dark) */
export const onGround = (bg: string) => (contrastRatio(bg, SUPPORT_INK) >= 4.5 ? SUPPORT_INK : SUPPORT_PAPER)

/** The colours a composition draws with, on top of the palette: the ground (a tint or a warm paper on « Papier » only —
    « Votre couleur » and « Encre » keep theirs), the band and its text. A ground that would not carry ink falls back. */
export function templateColours(d: SupportDesign): { bg: string; text: string; accent: string; muted: string; band: string; onBand: string } {
  const p = supportPalette(d)
  const t = templateOf(d)
  let bg = p.bg
  if (d.bg === 'paper') {
    const want = t === 'editorial' ? '#FBF7EF' : t === 'douceur' ? tint(d.accent, 0.9) : p.bg
    if (contrastRatio(want, SUPPORT_INK) >= 4.5) bg = want
  }
  const text = onGround(bg)
  const muted = text === SUPPORT_INK ? '#6B7268' : 'rgba(255,255,255,.78)'
  const accent = d.bg === 'brand' ? p.accent : (contrastRatio(bg, d.accent) >= 3 ? d.accent : text)
  /* the band: « Contraste » fills it with the accent; « Douceur », « Mise en avant » and « Photo » with a tint of it */
  const band = t === 'contraste' ? (d.bg === 'brand' ? text : d.accent) : d.bg === 'ink' ? '#3A423A' : tint(d.accent, t === 'photo' ? 0.86 : t === 'douceur' ? 0.78 : 0.82)
  const onBand = t === 'contraste' && d.bg === 'brand' ? bg : onGround(band)
  return { bg, text, accent, muted, band, onBand }
}

/* ---------- WF-089 · the saved models, this browser's ---------- */
export interface SavedTemplate { id: string; name: string; design: SupportDesign; savedAt: string }
const key = () => `bmr.modeles.${currentWorld()}`
export function savedTemplates(): SavedTemplate[] {
  try { const raw = localStorage.getItem(key()); if (raw) { const o = JSON.parse(raw) as SavedTemplate[]; if (Array.isArray(o)) return o } } catch { /* fine */ }
  return []
}
function write(list: SavedTemplate[]): void { try { localStorage.setItem(key(), JSON.stringify(list)) } catch { /* fine */ } }
/** « Enregistrer le modèle »: the style and the texts, never a token (the QR is always the new support's own). */
export function saveTemplate(name: string, design: SupportDesign): SavedTemplate {
  const t: SavedTemplate = { id: `mod-${Date.now().toString(36)}`, name: name.trim(), design: { ...design, texts: { ...design.texts } }, savedAt: new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' }) }
  write([...savedTemplates(), t])
  return t
}
export function removeTemplate(id: string): void { write(savedTemplates().filter(t => t.id !== id)) }
